<template>
	<app-content class="h-panel ai-agent">
		<div class="h-panel-bar">
			<span class="h-panel-title">财税智能体</span>
			<span class="h-tag" :class="status.enabled ? 'h-tag-bg-green' : 'h-tag-bg-red'" style="margin-left: 10px;">
				{{ status.enabled ? status.model : '未配置' }}
			</span>
		</div>

		<div class="chat-container">
			<!-- 消息列表 -->
			<div ref="msgList" class="msg-list">
				<div v-for="(msg, idx) in messages" :key="idx" class="msg-item" :class="msg.role">
					<div class="msg-avatar">
						<i v-if="msg.role === 'user'" class="h-icon-user"></i>
						<i v-else class="h-icon-star"></i>
					</div>
					<div class="msg-body">
						<div class="msg-content" v-html="formatContent(msg.content)"></div>

						<!-- 审批卡片 -->
						<div v-if="msg.approval" class="approval-card">
							<div class="approval-header">
								<i class="h-icon-warning"></i>
								<span>操作审批</span>
							</div>
							<div class="approval-body">
								<pre>{{ msg.approval.summary }}</pre>
							</div>
							<div class="approval-actions">
								<Button color="green" size="s" :loading="approving" @click="doConfirm(msg)">确认执行</Button>
								<Button color="red" size="s" :loading="approving" @click="doCancel(msg)">取消</Button>
							</div>
						</div>

						<div class="msg-time">{{ msg.time }}</div>
					</div>
				</div>

				<div v-if="loading" class="msg-item assistant">
					<div class="msg-avatar"><i class="h-icon-star"></i></div>
					<div class="msg-body">
						<div class="msg-content loading-dots">正在思考...</div>
					</div>
				</div>
			</div>

			<!-- 输入区域 -->
			<div class="input-area">
				<div class="input-box">
					<textarea
						v-model="input"
						placeholder="请输入您的问题，例如：帮我录入一张报销凭证..."
						@keydown.enter.exact.prevent="send"
						@keydown.enter.shift.exact.prevent="input += '\n'"
					></textarea>
					<Button color="primary" :loading="loading" @click="send">发送</Button>
				</div>
				<div class="input-tip">Enter 发送，Shift + Enter 换行</div>
			</div>
		</div>
	</app-content>
</template>

<script>
import moment from 'moment';

export default {
	name: 'AiAgent',
	data() {
		return {
			status: { enabled: false, model: '', baseUrl: '' },
			messages: [],
			input: '',
			loading: false,
			approving: false
		};
	},
	mounted() {
		this.loadStatus();
		this.addWelcome();
	},
	methods: {
		loadStatus() {
			this.$api.ai.status().then(({ data }) => {
				this.status = data || {};
			}).catch(() => {});
		},
		addWelcome() {
			this.messages.push({
				role: 'assistant',
				content: '您好！我是财税智能体"小税"。我可以帮您：\n\n- 查询账套、科目、余额、明细账\n- 校验和保存记账凭证\n- 检索和调用系统接口\n- 记住您的记账规则\n\n请告诉我您需要做什么？',
				time: this.now()
			});
		},
		now() {
			return moment().format('HH:mm:ss');
		},
		formatContent(text) {
			if (!text) return '';
			return text.replace(/\n/g, '<br>');
		},
		buildPayload() {
			// 只发送 role + content 的历史消息
			return this.messages
				.filter(m => m.role === 'user' || m.role === 'assistant')
				.map(m => ({ role: m.role, content: m.content }));
		},
		send() {
			const text = this.input.trim();
			if (!text || this.loading) return;

			//输入"确认/取消"时，如存在待审批卡片则直接转为审批操作，避免大模型重复生成审批单
			if (!this.approving) {
				const pending = [...this.messages].reverse().find(m => m.approval);
				if (pending && /^(确认|确定|是的|好|保存|入账)$/.test(text)) {
					this.input = '';
					this.doConfirm(pending);
					return;
				}
				if (pending && /^(取消|放弃|算了|不用了)$/.test(text)) {
					this.input = '';
					this.doCancel(pending);
					return;
				}
			}

			this.messages.push({ role: 'user', content: text, time: this.now() });
			this.input = '';
			this.loading = true;
			this.scrollToBottom();

			this.$api.ai.chat({ messages: this.buildPayload() }).then(({ data }) => {
				this.loading = false;
				if (data && data.reply) {
					this.messages.push({
						role: 'assistant',
						content: data.reply,
						approval: data.approval || null,
						time: this.now()
					});
				} else {
					this.messages.push({
						role: 'assistant',
						content: '抱歉，我没有得到有效的回复。',
						time: this.now()
					});
				}
				this.scrollToBottom();
			}).catch(() => {
				this.loading = false;
				this.messages.push({
					role: 'assistant',
					content: '服务异常，请稍后重试。',
					time: this.now()
				});
				this.scrollToBottom();
			});
		},
		doConfirm(msg) {
			if (!msg.approval || this.approving) return;
			this.approving = true;
			this.$api.ai.chat({
				messages: [],
				confirmToken: msg.approval.approvalToken
			}).then(({ data }) => {
				this.approving = false;
				msg.approval = null; // 移除审批卡片
				this.messages.push({
					role: 'assistant',
					content: data.reply || '操作已确认执行。',
					time: this.now()
				});
				this.scrollToBottom();
			}).catch(() => {
				this.approving = false;
				this.$Message.error('确认失败');
			});
		},
		doCancel(msg) {
			if (!msg.approval || this.approving) return;
			this.approving = true;
			this.$api.ai.chat({
				messages: [],
				cancelToken: msg.approval.cancelToken
			}).then(({ data }) => {
				this.approving = false;
				msg.approval = null;
				this.messages.push({
					role: 'assistant',
					content: data.reply || '操作已取消。',
					time: this.now()
				});
				this.scrollToBottom();
			}).catch(() => {
				this.approving = false;
				this.$Message.error('取消失败');
			});
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const el = this.$refs.msgList;
				if (el) {
					el.scrollTop = el.scrollHeight;
				}
			});
		}
	}
};
</script>

<style lang="less" scoped>
.ai-agent {
	display: flex;
	flex-direction: column;
	height: calc(100vh - 100px);
}

.chat-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	overflow: hidden;
}

.msg-list {
	flex: 1;
	overflow-y: auto;
	padding: 16px;
	background: #f9f9f9;
}

.msg-item {
	display: flex;
	margin-bottom: 16px;

	&.user {
		flex-direction: row-reverse;

		.msg-body {
			background: #e8f4ff;
			border-color: #b6d8ff;
		}

		.msg-avatar {
			margin-left: 8px;
			margin-right: 0;
			background: #2d8cf0;
			color: #fff;
		}
	}

	&.assistant {
		.msg-avatar {
			background: #52c41a;
			color: #fff;
		}
	}
}

.msg-avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	margin-right: 8px;
	flex-shrink: 0;
}

.msg-body {
	max-width: 75%;
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 10px 14px;
	position: relative;
}

.msg-content {
	line-height: 1.6;
	word-break: break-word;
}

.msg-time {
	font-size: 12px;
	color: #999;
	margin-top: 6px;
	text-align: right;
}

.loading-dots::after {
	content: '...';
	animation: dots 1.2s steps(4, end) infinite;
}

@keyframes dots {
	0%, 20% { content: '.'; }
	40% { content: '..'; }
	60%, 100% { content: '...'; }
}

.approval-card {
	margin-top: 10px;
	border: 1px solid #ffa940;
	border-radius: 6px;
	background: #fff7e6;
	overflow: hidden;
}

.approval-header {
	background: #ffa940;
	color: #fff;
	padding: 6px 12px;
	font-weight: bold;
	font-size: 13px;

	i {
		margin-right: 6px;
	}
}

.approval-body {
	padding: 10px 12px;

	pre {
		white-space: pre-wrap;
		word-break: break-all;
		font-size: 13px;
		line-height: 1.5;
		margin: 0;
	}
}

.approval-actions {
	padding: 8px 12px;
	border-top: 1px solid #ffe7ba;
	display: flex;
	gap: 8px;
}

.input-area {
	border-top: 1px solid #e8e8e8;
	padding: 12px 16px;
	background: #fff;
}

.input-box {
	display: flex;
	gap: 8px;

	textarea {
		flex: 1;
		min-height: 44px;
		max-height: 120px;
		padding: 10px;
		border: 1px solid #d9d9d9;
		border-radius: 6px;
		resize: vertical;
		font-size: 14px;
		line-height: 1.5;

		&:focus {
			border-color: #2d8cf0;
			outline: none;
		}
	}

	button {
		align-self: flex-end;
		height: 36px;
	}
}

.input-tip {
	font-size: 12px;
	color: #999;
	margin-top: 6px;
	text-align: right;
}
</style>
